import { Injectable } from '@angular/core'
import { NetworkData, NetworkState } from '../interfaces/network'
import { BehaviorSubject, interval, Subscription } from 'rxjs'
import { PlatformService } from '../platforms/platform.service'
import { LogService } from 'htv-lib'
import { LoaderService } from './loader.service'

@Injectable({
  providedIn: 'root',
})
export class NetworkService {
  isConnected = false

  networkState?: NetworkState
  networkState$!: BehaviorSubject<NetworkState>
  networkStateSubscription?: Subscription

  networkData?: NetworkData
  networkData$ = new BehaviorSubject<NetworkData | undefined>(undefined)

  constructor(
    private loaderService: LoaderService,
    private logService: LogService,
    private platformService: PlatformService
  ) {
    // do nothing
  }

  async init(): Promise<void> {
    this.logService.info(`[NetworkService] Init`)

    this.networkStateSubscription?.unsubscribe()

    this.loaderService.setDebugStep('PAGES.LOADER.INITIALIZING_NETWORK')
    const networkState = await this.platformService.getNetworkState()
    this.loaderService.clearStep()
    this.updateData(networkState)

    this.networkState$ = new BehaviorSubject<NetworkState>(networkState)
    this.networkStateSubscription = this.networkState$.subscribe(networkState => {
      this.updateData(networkState)
    })
    this.platformService.registerNetworkStateListener(this.networkState$)
  }

  async updateData(networkState: NetworkState): Promise<void> {
    if (this.networkState != networkState) {
      this.networkState = networkState
      this.isConnected = this.networkState == NetworkState.CONNECTED
      this.logService.info(`[NetworkService] state: ${this.networkState}`)
      try {
        this.networkData = await this.platformService.getNetworkData()
      } catch (err: any) {
        this.networkData = undefined
      }
      this.networkData$.next(this.networkData)
    }
  }

  waitForConnection(): Promise<void> {
    return new Promise((resolve, reject) => {
      let count = 0
      if (this.isConnected) {
        return resolve()
      }
      this.loaderService.setDebugStep('PAGES.LOADER.WAITING_FOR_NETWORK')
      const sub = interval(1000).subscribe(() => {
        count++
        if (this.isConnected) {
          sub.unsubscribe()
          this.loaderService.clearStep()
          return resolve()
        }
        if (count > 5) {
          sub.unsubscribe()
          this.loaderService.clearStep()
          return reject()
        }
      })
    })
  }

  getNetworkData(): NetworkData | undefined {
    return this.networkData
  }

  getMACAddress(): string {
    return this.networkData?.macAddress ?? ''
  }

  getIPAddress(): string {
    return this.networkData?.ipAddress ?? ''
  }
}
